<script setup>
import { Course } from '@pfsc/core/models/course';
import { Learner } from '@pfsc/core/models/learner';
import { DEFAULT_PAGINATION_INFO, PaginationInfo } from '@pfsc/core/models/pagination-info';
import { UserCreation } from '@pfsc/core/models/user';
import { eLearningService } from '@pfsc/core/services/e-learning.service';
import { learnerService } from '@pfsc/core/services/learner.service';
import { VISIBILITY_NUMERIC_VALUE } from '@pfsc/core/models/visibility';
import { TableQueryParams } from '@pfsc/core/services/table-query-params.service';
import { userService } from '@pfsc/core/services/user.service';
import { useAuthStore } from '@pfsc/core/stores/auth.store';
import { useCoursesRequireDobStore } from '@pfsc/core/stores/courses-require-dob.store';
import { useTriggerStore } from '@pfsc/core/stores/trigger.store';
import { DEFAULT_LEARNER_COLUMNS, getLearnersColumnsWithAdditionalFields } from '@pfsc/core/ui-services/learner.service';
import { notificationService } from '@pfsc/core/ui-services/notification.service';
import { queryParamsService } from '@pfsc/core/ui-services/query-params.service';
import { transferPackageMixin } from '@pfsc/core/ui-services/transfer-package.mixin';
import { useLearnerAssignValidation } from '@pfsc/core/ui-services/use-learner-assign-validation';
import { useLearnersSelectValidation } from '@pfsc/core/ui-services/use-learners-select-validation';
import { CALIFORNIA_RESPONSIBLE_BEVERAGE_SERVICE_TRAINING_ID } from '@pfsc/core/models/package';
import { toggleExecutionState } from '@pfsc/core/utils/toggleExecutionState';
import { globalTranslate } from '@pfsc/shared/locale/app.i18n';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useMobileDetection } from '@pfsc/core/ui-services/use-mobile-detection';
import UnassignedLearnersTableForMobile from '../unassigned-learners-table-for-mobile.vue';
import AppDatePicker from '../app-date-picker.vue';
import AppDialog from '../app-dialog.vue';
import AppPagination from '../app-pagination.vue';
import UnassignedLearnersTable from '../unassigned-learners-table.vue';
import RbsInformation from '../rbs-information.vue';

const trigger = useTriggerStore();
const authStore = useAuthStore();
const { currentUser, groupAdmin } = storeToRefs(authStore);
const { isMobile } = useMobileDetection();

const dialogRef = ref(null);
const isLoading = ref(false);

/** @type {import('vue').Ref<Course>} */
const course = ref(null);

/** @type {import('vue').Ref<PaginationInfo>} */
const pagination = ref({ ...DEFAULT_PAGINATION_INFO });

/** @type {import('vue').Ref<TableQueryParams>} */
const queryParams = ref(/** @type {TableQueryParams} */ (/** @type {unknown} */ ({
  ...queryParamsService.getDefaultQueryParams(),
  searchString: '',
  groupAdmin: groupAdmin.value,
  visibility: { value: VISIBILITY_NUMERIC_VALUE.UNHIDDEN },
})));

const { isCourseRequireDob } = useCoursesRequireDobStore();

const isCourseRequiredDateOfBirth = computed(
  () => {
    if (course.value == null) {
      return false;
    }
    return isCourseRequireDob(course.value.course);
  },
);

const {
  clonedLearnersList,
  createValidatorForClonedSelectedLearners,
  getValidatorsForField,
  validateFormAndExecute,
} = useLearnerAssignValidation(isCourseRequiredDateOfBirth);

const {
  clearServerError, serverError,
} = useLearnersSelectValidation({
  isRbsServerIdRequired: ref(false),
  isCourseRequiredDateOfBirth: ref(false),
  isAddLearner: ref(true),
});

const isCaliforniaResponsibleBeverageServiceTrainingId = computed(
  () => course.value?.package.id
    === CALIFORNIA_RESPONSIBLE_BEVERAGE_SERVICE_TRAINING_ID,
);

const columns = computed(() => getLearnersColumnsWithAdditionalFields({
  defaultColumns: DEFAULT_LEARNER_COLUMNS,
  hasDobColumn: isCourseRequiredDateOfBirth.value,
  hasAssignColumn: true,
  hasRbsColumn: isCaliforniaResponsibleBeverageServiceTrainingId.value,
}));

const mobileColumns = computed(() => {
  const columnTypeMap = {
    dateOfBirth: 'date',
    rbsServerId: 'text-field',
    instructorNotes: 'text-field',
    lastName: isCaliforniaResponsibleBeverageServiceTrainingId.value ? 'text-field' : 'text',
  };

  const shortNameMap = {
    dateOfBirth: globalTranslate('assignment.shortDob'),
    email: globalTranslate('header.email'),
    rbsServerId: globalTranslate('assignment.rbsServerId'),
  };

  return columns.value.map(column => ({
    ...column,
    title: shortNameMap[column.key] ?? column.title,
    type: columnTypeMap[column.key] ?? 'text',
  }));
});

/** Prepare learners. */
async function prepareLearners() {
  await toggleExecutionState(isLoading, async () => {
    pagination.value = await learnerService.getLearners({
      options: queryParams.value,
      packageId: course.value.package.id,
      groupAdmin: groupAdmin.value,
      excludedUsers: [course.value.registration.user],
    });
    clonedLearnersList.value = pagination.value.data;
    createValidatorForClonedSelectedLearners(clonedLearnersList.value);
  });
}

const { onPageChange, onSearchChange, resetPageQueryParams } = transferPackageMixin({ queryParams, prepareLearners });

/**
 * Open dialog.
 * @param {Course} value Course
 */
async function openDialog(value) {
  course.value = value;

  dialogRef.value.openDialog();
  await prepareLearners();
}

/** Handle on dialog close. */
function onCloseDialog() {
  resetPageQueryParams();
}

/** Close dialog. */
function closeDialog() {
  dialogRef.value.closeDialog();
  onCloseDialog();
}

/**
 * Transfer course
 * @param {Learner} learner Learner.
 */
async function transferCourse(learner) {
  validateFormAndExecute(learner.id, async () => {
    await toggleExecutionState(isLoading, async () => {
      await userService.updateUserInfo({
        userId: learner.id,
        updatedData: new UserCreation({ dob: learner.dateOfBirth, rbsServerId: learner.rbsServerId }),
        groupAdmin: groupAdmin.value,
        isManager: currentUser.value.isPfsAdmin || currentUser.value.isGroupAdmin,
      });
      await eLearningService.transferCourse(learner, course.value);
      trigger.triggerRegistrationsChange();
      notificationService.notifyCommonSuccess();
      dialogRef.value.closeDialog();
    });
  });
}

defineExpose({ openDialog, closeDialog });
</script>

<template>
  <app-dialog
    ref="dialogRef"
    width="100%"
    :title="globalTranslate('transferCourseDialog.title')"
    @close="onCloseDialog"
  >
    <section :class="$style.section">
      <div v-if="isCaliforniaResponsibleBeverageServiceTrainingId">
        <rbs-information />
      </div>
      <v-text-field
        :model-value="queryParams.searchString"
        :label="globalTranslate('search')"
        hide-details
        density="comfortable"
        variant="solo-filled"
        :class="$style.search"
        append-inner-icon="fas fa-solid fa-search"
        @update:model-value="onSearchChange"
      />
      <unassigned-learners-table
        v-if="!isMobile"
        :headers="columns"
        :is-loading="isLoading"
        :show-select="false"
        :items="pagination.data"
        :items-per-page="queryParams.perPage"
        :action-button-title="globalTranslate('transferCourseDialog.assign')"
        fixed-header
        @on-action="transferCourse"
      >
        <template #[`item.dateOfBirth`]="{ internalItem: item }">
          <app-date-picker
            v-model="item.raw.dateOfBirth"
            density="compact"
            :error-messages="
              getValidatorsForField('dateOfBirth', item.raw.id)?.errors
            "
            @blur="getValidatorsForField('dateOfBirth', item.raw.id)?.touch"
            @update:model-value="getValidatorsForField('dateOfBirth', item.raw.id)?.touch"
          />
        </template>
        <template #[`item.rbsServerId`]="{ internalItem: item }">
          <v-text-field
            v-model="item.raw.rbsServerId"
            density="compact"
            variant="outlined"
            rounded="lg"
            :error-messages="
              serverError[item.raw.id]?.rbsServerId || getValidatorsForField('rbsServerId', item.raw.id)?.errors
            "
            @blur="getValidatorsForField('rbsServerId', item.raw.id)?.touch"
            @input="() => {
              getValidatorsForField('rbsServerId', item.raw.id)?.touch;
              clearServerError(item.raw.id)
            }"
          />
        </template>
      </unassigned-learners-table>

      <unassigned-learners-table-for-mobile
        v-else
        :headers="mobileColumns"
        :is-show-full-info="true"
        :items="pagination.data"
        :mobile-cell-column="{key: 'lastName', sortable: false, title: globalTranslate('assignment.learnerInfo')}"
        :is-loading="isLoading"
        :show-select="false"
        :has-dob-column="isCourseRequiredDateOfBirth"
        :is-rbs-package="isCaliforniaResponsibleBeverageServiceTrainingId"
        :action-button-title="globalTranslate('transferCourseDialog.assign')"
        :get-validators-for-field="getValidatorsForField"
        @on-action="transferCourse"
      >
        <template #[`item.dateOfBirth`]="{ item }">
          <app-date-picker
            v-model="item.raw.dateOfBirth"
            density="compact"
            hide-details="auto"
            :class="$style.inlineMobileField"
            :error-messages="
              getValidatorsForField('dateOfBirth', item.raw.id)?.errors
            "
            @blur="getValidatorsForField('dateOfBirth', item.raw.id)?.touch"
            @update:model-value="getValidatorsForField('dateOfBirth', item.raw.id)?.touch"
          />
        </template>
        <template #[`item.rbsServerId`]="{ item }">
          <v-text-field
            v-model="item.raw.rbsServerId"
            density="compact"
            variant="outlined"
            rounded="lg"
            :class="$style.inlineMobileField"
            :error-messages="
              serverError[item.raw.id]?.rbsServerId || getValidatorsForField('rbsServerId', item.raw.id)?.errors
            "
            @blur="getValidatorsForField('rbsServerId', item.raw.id)?.touch"
            @input="() => {
              getValidatorsForField('rbsServerId', item.raw.id)?.touch;
              clearServerError(item.raw.id)
            }"
          />
        </template>
      </unassigned-learners-table-for-mobile>

      <app-pagination
        :page="pagination.currentPage"
        :total="pagination.total"
        :per-page="queryParams.perPage"
        :total-pages="pagination.totalPages"
        @on-page-change="onPageChange"
      />
    </section>
  </app-dialog>
</template>

<style module>

.section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.action {
  width: 100%;
}

.search {
  max-width: 400px;
  width: 100%;
  align-self: flex-end;
}

.button {
  width: 100%;
}

.inlineMobileField {
  flex: 1;
  min-width: 160px;
  max-width: 270px;
}

</style>
